import { useState } from 'react';
import useGlobalFilters from '../../../../hooks/useGlobalFilters';
import useTableMenu from '../../../../hooks/useTableMenu';
import useUserAccess from '../../../../hooks/useUserAccess';
import { getAccessValues } from '../../../../components/layout/RoutesManager/access';
import { useDispatch } from 'react-redux';
import { showAlert } from '../../../../features/slices/applicationSlice';
import { useTranslation } from 'react-i18next';
import useTranslate from '../../../../hooks/useTranslate';
import {
    useGetRGRLVinculationPresentationsQuery,
    GenerateFilesBody,
    useGenerateFilesMutation,
    useGetRGRLVinculationContractsQuery,
    useGetRGRLVinculationEstablishmentsQuery
} from '../../../../services/rgrlVinculationApi';
import useDownloadFile from '../../../../hooks/useDownloadFile';
import { PdfType } from '../../../Presentaciones/hooks/useReports';
import { useLazyGetPresentacionRGRLPDFbyIdQuery } from '../../../../services/presentacionesApi';
import { downloadBase64File, getCurrentDate, getDateTextFieldFormat } from '../../../../utils/common';
import { enqueueSnackbar } from 'notistack';
import { useAppSelector } from '../../../../hooks/reduxHooks';
import dayjs from 'dayjs';
import { SRT_STATES } from '../../consts/states';
import { useRoles } from '../../../../hooks/useRoles';
import { useOrganizationFilter } from '../../../../contexts/OrganizationFilterContext';
interface SelectedPresentation {
    id: number;
    estadoSRT: SRT_STATES;
}

const useFileGenerationRGRL = (handleCloseDrawer: any) => {
    const dispatch = useDispatch();
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    
    const [selectedItemFromAction, setSelectedItemFromAction] = useState<any>(null);
    const { isDownloading, downloadFile } = useDownloadFile();
    const { isPerfilBackOffice } = useRoles();
    const presentationUploadErrors = useAppSelector(
        (state) => state.srtPresentations.presentationUploadErrorLocationData
    );
    const [selected, setSelected] = useState<any | null>(null);
    const [selectedPresentations, setSelectedPresentations] = useState<SelectedPresentation[]>([]);

    const filters = useGlobalFilters();
    const { filters: baseFilters, extraFilters, setFilters, handleSubmitSearch } = filters;

    const { data: contractsOptions, isFetching: isFetchingContractsOptions } =
        useGetRGRLVinculationContractsQuery({ ...(isPerfilBackOffice ? extraFilters : {}) });
    
    const { data: establishmentsOptions, isFetching: isFetchingEstablishmentsOptions } =
        useGetRGRLVinculationEstablishmentsQuery(extraFilters);
    
    const { t } = useTranslation('filesGenerationSRT');
    const { translate } = useTranslate(t);

    const tableMenu = useTableMenu();
    const access = useUserAccess();
    const FEATURES = getAccessValues(access);

    const { handleOpenCard, anchorToEventElement, openMenu, closeMenu } = tableMenu;
    const { data: presentations, isFetching: isFetchingPresentations } =
        useGetRGRLVinculationPresentationsQuery({
            ...baseFilters,
            fechaInicio: startDate ? dayjs(startDate).startOf('day').toISOString() : undefined,
            fechaFin: endDate ? dayjs(endDate).endOf('day').toISOString() : undefined
        });
    const [generateFiles, { isLoading: isGeneretingFiles }] = useGenerateFilesMutation();
    const [isDownloadingPDF, setDownloadingPDF] = useState<boolean>(false);
    const [triggerGetPresentacionRGRLPDFById, { isLoading: isLoadingRgrl }] =
        useLazyGetPresentacionRGRLPDFbyIdQuery();
    
    const { selectedOrganization } = useOrganizationFilter();

    const handleDownloadPresentation = async () => {
        try {
            closeMenu();
            setDownloadingPDF(true);
            if (
                !selectedItemFromAction?.id ||
                selectedItemFromAction?.tipo?.nombre !== PdfType.RGRL
            ) {
                enqueueSnackbar(translate('table.enqueues.error.downloadPDF'), {
                    variant: 'error'
                });
                return;
            }
            let fileName = `${
                selectedItemFromAction?.establecimiento?.nombre || 'Establecimiento'
            } - Cod SRT_${selectedItemFromAction?.establecimiento?.codigoSRT || 'CodigoSRT'} -  ${
                PdfType.RGRL
            }`;

            let response: any = null;
            response = await triggerGetPresentacionRGRLPDFById({
                idPresentacion: selectedItemFromAction?.id,
                typeId: selectedItemFromAction?.tipo?.id
            });

            if (response?.error?.data?.statusCode === 404) {
                enqueueSnackbar(translate('table.enqueues.error.notFoundPDF'), {
                    variant: 'error'
                });
                return;
            }

            if (response?.data?.data?.presignedUrls?.length >= 1) {
                response.data.data.presignedUrls.forEach((url: string, index: number) => {
                    downloadBase64File(url, selectedItemFromAction.id, fileName, index);
                    enqueueSnackbar(translate('table.enqueues.success.foundPDF'), {
                        variant: 'success'
                    });
                });
            }
        } catch (error) {
        } finally {
            setDownloadingPDF(false);
        }
    };

    const generateAndDownloadFiles = async () => {
        try {
            if (selectedPresentations?.length <= 0) {
                enqueueSnackbar(
                    translate('drawers.generateFilesSRT.enqueues.warning.noPresentationSelected'),
                    {
                        variant: 'warning'
                    }
                );
                return;
            }
            const presentationsIds = selectedPresentations.map((presentation) => presentation?.id);
            const adaptedBody: GenerateFilesBody = {
                idsPresentacion: presentationsIds
            };
            const response: any = await generateFiles(adaptedBody);
            if (
                response?.error?.data?.statusCode === 400 &&
                response?.error?.data?.message ===
                    'Ha superado el límite de generación de archivos diarios.'
            ) {
                showGenerationLimitAlert();
                return;
            }
            if (
                response?.error?.data?.statusCode === 500 &&
                response?.error?.data?.message === 'Error al generar archivos.'
            ) {
                enqueueSnackbar(
                    translate('drawers.generateFilesSRT.enqueues.error.somethingWentWrong'),
                    {
                        variant: 'error'
                    }
                );
                return;
            }

            if (response.data.data.presignedUrl) {
                const todayDate = getCurrentDate();
                const formatDate = getDateTextFieldFormat(todayDate);
                const fileName = `${t('drawers.generateFilesSRT.filename')}_${formatDate}`;
                await downloadFile(response.data.data.presignedUrl, '.zip', fileName);
            }

            enqueueSnackbar(translate('drawers.generateFilesSRT.enqueues.success.generated'), {
                variant: 'success'
            });
            setSelectedPresentations([]);
            handleCloseDrawer();
        } catch (error) {
            enqueueSnackbar(
                translate('drawers.generateFilesSRT.enqueues.error.somethingWentWrong'),
                {
                    variant: 'error'
                }
            );
        }
    };

    const handleRowClick = (selectedRowItem: any) => {
        setSelectedItemFromAction(selectedRowItem);
        setSelected(selectedRowItem);
        handleOpenCard();
    };

    const handleClickAction = (event: any, item: any, index: any) => {
        setSelectedItemFromAction(item);
        anchorToEventElement(event);
        openMenu();
    };

    const showGenerationLimitAlert = () => {
        dispatch(
            showAlert({
                title: translate('alerts.generationLimit.title'),
                message: translate('alerts.generationLimit.message'),
                icon: 'error',
                confirmText: translate('buttons.accept')
            })
        );
    };

    const handleItemCheck = (selectedPresentation: SelectedPresentation) => {
        if (isPerfilBackOffice && (!selectedOrganization || !selectedOrganization?.id)) { 
            
            enqueueSnackbar(translate('drawers.generateFilesSRT.enqueues.warning.noOrganizationSelected'), {
                variant: 'warning'
            });
            return;
        }

        if (selectedPresentation?.estadoSRT === SRT_STATES.ERROR) return;

        const updatedSectors = selectedPresentations.some(
            (presentation) => presentation.id === selectedPresentation.id
        )
            ? selectedPresentations.filter(
                  (presentation) => presentation.id !== selectedPresentation.id
              )
            : [...selectedPresentations, selectedPresentation];

        setSelectedPresentations(updatedSectors);
    };

    const handleDateChange = (startDate: Date | null, endDate: Date | null) => {
        setStartDate(startDate);
        setEndDate(endDate);

        const filtersWithDates = {
            ...filters,
            fechaInicio: startDate || undefined,
            fechaFin: endDate || undefined
        };
        handleCloseDrawer();
        setFilters(filtersWithDates);
        handleSubmitSearch();
    };

    return {
        filters,
        tableMenu,
        selectedItemFromAction,
        selected,
        FEATURES,
        selectedPresentations,
        presentations,
        isFetchingPresentations,
        isDownloadingPDF,
        isDownloading,
        isLoadingRgrl,
        isGeneretingFiles,
        presentationUploadErrors,
        contractsOptions,
        isFetchingContractsOptions,
        establishmentsOptions,
        isFetchingEstablishmentsOptions,
        startDate,
        endDate,
        setStartDate,
        setEndDate,
        handleDateChange,
        generateAndDownloadFiles,
        handleItemCheck,
        showGenerationLimitAlert,
        handleClickAction,
        handleRowClick,
        handleDownloadPresentation
    };
};

export default useFileGenerationRGRL;
